import { ReactElement } from 'react';
import { dateFormats, sensorUnits } from '../../constants';
import { userIsHbs } from '../findUserType';

export const userSettingsSelectors = {
    radonUnit: 'radonUnit',
    tempUnit: 'tempUnit',
    pressureUnit: 'pressureUnit',
    vocUnit: 'vocUnit',
    dateFormat: 'dateFormat',
    lengthUnit: 'lengthUnit',
};
type PreferenceOption = {
    settingId: string;
    options: { label: string; value: string }[];
    header: string;
};

const unitPreferenceOptions: PreferenceOption[] = [
    {
        settingId: userSettingsSelectors.radonUnit,
        options: [
            { label: sensorUnits.bq, value: 'bq' },
            { label: sensorUnits.pci, value: 'pci' },
        ],
        header: 'radonShortTermAvg',
    },
    {
        settingId: userSettingsSelectors.tempUnit,
        options: [
            { label: 'Units.UnitCelsius', value: 'c' },
            { label: 'Units.UnitFahrenheit', value: 'f' },
        ],
        header: 'Temperature',
    },
    {
        settingId: userSettingsSelectors.pressureUnit,
        options: [
            { label: sensorUnits.hpa, value: 'hpa' },
            { label: sensorUnits.inhg, value: 'inhg' },
        ],
        header: 'pressure',
    },
    {
        settingId: userSettingsSelectors.vocUnit,
        options: [
            { label: sensorUnits.ppb, value: 'ppb' },
            { label: sensorUnits.mgpc, value: 'mgpc' },
        ],
        header: 'voc',
    },
];

const preferenceOptions = [
    ...unitPreferenceOptions,
    {
        settingId: userSettingsSelectors.dateFormat,
        options: [
            { label: 'DateFormattingDDMMYYYY', value: dateFormats.EUR.type },
            { label: 'DateFormattingMMDDYYYY', value: dateFormats.US.type },
        ],
        header: 'DateFormat',
    },
];
if (userIsHbs()) {
    preferenceOptions.push({
        settingId: userSettingsSelectors.lengthUnit,
        options: [
            { label: 'Units.UnitMeter', value: 'm' },
            { label: 'Units.UnitFeet', value: 'ft' },
        ],
        header: 'LengthUnit',
    });
}

const getSelectorOptions = (
    settingsId: string,
    options: { label: string; value: string }[]
): { label: string; value: string }[] => {
    if (settingsId !== userSettingsSelectors.vocUnit || userIsHbs()) {
        return options;
    }
    return options.filter(option => option.value === 'ppb');
};

export const getUnitPreferenceSelectors = (
    settingsRow: (
        settingId: string,
        options: { label: string; value: string }[],
        header: string,
        selectedValue: string
    ) => ReactElement,
    selectedRadonUnit: string,
    selectedTempUnit: string,
    selectedPressureUnit: string,
    selectedVocUnit: string,
    excludedSensors: string[] = []
): ReactElement[] => {
    const filteredOptions = unitPreferenceOptions.filter(option => !excludedSensors.includes(option.settingId));
    return filteredOptions.map(preference => {
        let selectorValue = selectedRadonUnit;
        if (preference.settingId === userSettingsSelectors.pressureUnit) selectorValue = selectedPressureUnit;
        if (preference.settingId === userSettingsSelectors.tempUnit) selectorValue = selectedTempUnit;
        if (preference.settingId === userSettingsSelectors.vocUnit) selectorValue = selectedVocUnit || 'ppb';
        const options = getSelectorOptions(preference.settingId, preference.options);
        return settingsRow(preference.settingId, options, preference.header, selectorValue);
    });
};

const getUserPreferenceSelectors = (
    settingsRow: (
        settingId: string,
        options: { label: string; value: string }[],
        header: string,
        selectedValue: string
    ) => ReactElement,
    selectedRadonUnit: string,
    selectedTempUnit: string,
    selectedPressureUnit: string,
    selectedVocUnit: string,
    selectedDateFormat: string,
    selectedMeasurementSystem?: string
): ReactElement[] =>
    preferenceOptions.map(preference => {
        let selectorValue = selectedRadonUnit;
        if (preference.settingId === userSettingsSelectors.dateFormat) selectorValue = selectedDateFormat;
        if (preference.settingId === userSettingsSelectors.pressureUnit) selectorValue = selectedPressureUnit;
        if (preference.settingId === userSettingsSelectors.tempUnit) selectorValue = selectedTempUnit;
        if (preference.settingId === userSettingsSelectors.vocUnit) selectorValue = selectedVocUnit;
        if (preference.settingId === userSettingsSelectors.lengthUnit) selectorValue = selectedMeasurementSystem || 'm';
        const options = getSelectorOptions(preference.settingId, preference.options);
        return settingsRow(preference.settingId, options, preference.header, selectorValue);
    });

export default getUserPreferenceSelectors;
